import { HEADER_SAFE_SEGMENT } from './organization';

// Branded organization identity.
//
// A bare string cannot be an OrganizationId: values enter through the
// sanctioned producers below, which makes every organization value in the
// codebase traceable to either the acting route (OrganizationScope /
// useCommittedOrganization) or a validated loaded record
// (asTargetOrganization). Lint rejects assertions outside this module that
// mention OrganizationId (bare, array, generic, and nested forms) plus
// `as any` initializers on OrganizationId-annotated bindings; the check is
// syntactic, so multi-step type laundering is a review concern, not a lint
// guarantee.
declare const organizationIdBrand: unique symbol;
export type OrganizationId = string & { readonly [organizationIdBrand]: 'OrganizationId' };

export class InvalidOrganizationNameError extends Error {
  constructor(value: string) {
    super(`Value cannot be an organization identity: ${JSON.stringify(value)}`);
    this.name = 'InvalidOrganizationNameError';
  }
}

// Same envelope rule as route derivation: the value must be legal as an
// X-Astur-Organization header, or Headers.set would throw per request.
export const isValidOrganizationName = (value: string): boolean => value.length > 0 && value.trim() === value && HEADER_SAFE_SEGMENT.test(value);

export const asOrganizationId = (value: string): OrganizationId => {
  if (!isValidOrganizationName(value)) throw new InvalidOrganizationNameError(value);
  return value as OrganizationId;
};

export const toOrganizationId = (value: string | null | undefined): OrganizationId | null =>
  value != null && isValidOrganizationName(value) ? (value as OrganizationId) : null;

// An administered tenant, captured whole from a validated loaded record on a
// cross-target admin surface. `name` is what cross-target headers carry;
// `id` is what body-authoritative payload rows carry. Never rebuild either
// from mutable form values, allowance rows, or a later router read.
export type TargetOrganization = Readonly<{ id: number; name: OrganizationId }>;

export const asTargetOrganization = (record: { id: number | string; name: string }): TargetOrganization => {
  const id = typeof record.id === 'string' ? Number.parseInt(record.id, 10) : record.id;
  if (!Number.isInteger(id) || id <= 0) {
    throw new InvalidOrganizationNameError(String(record.id));
  }
  return Object.freeze({ id, name: asOrganizationId(record.name) });
};

// The three orthogonal request-scope axes (see
// docs/organization-identity/contract-matrix.md). Tenant scope says what the
// X-Astur-Organization header means; auth policy is independent of it;
// transport says which client carries the request.
export type TenantScope = { kind: 'organization'; organization: OrganizationId } | { kind: 'none' };

export const validateTenantScope = (value: unknown): TenantScope => {
  if (!value || typeof value !== 'object') throw new InvalidOrganizationNameError(String(value));
  const candidate = value as { kind?: unknown; organization?: unknown };
  if (candidate.kind === 'none' && candidate.organization === undefined) return NO_TENANT_SCOPE;
  if (candidate.kind === 'organization' && typeof candidate.organization === 'string' && isValidOrganizationName(candidate.organization)) {
    return { kind: 'organization', organization: asOrganizationId(candidate.organization) };
  }
  throw new InvalidOrganizationNameError(String(candidate.organization ?? candidate.kind));
};

export type AuthPolicy = 'session' | 'anonymous';

export type TransportKind = 'api' | 'channelApi' | 'external';

export const organizationScope = (organization: OrganizationId): TenantScope => ({ kind: 'organization', organization });

export const NO_TENANT_SCOPE: TenantScope = Object.freeze({ kind: 'none' });
